import { useState, useEffect, useCallback, useRef } from "react";

const DB_NODES = [
  { key:"pri", label:"FSA-DB-Primary",   host:"1.7.187.57:27017", url:"/fsadb-api"  },
  { key:"sec", label:"FSA-DB-Secondary", host:"1.7.187.58:27017", url:"/fsadb2-api" },
  { key:"ter", label:"FSA-DB-Tertiary",  host:"1.7.187.59:27017", url:"/fsadb3-api" },
];
const PRI = DB_NODES[0].url;

function fmt(mb) {
  if (!mb) return "0 MB";
  if (mb>=1048576) return (mb/1048576).toFixed(2)+" TB";
  if (mb>=1024)    return (mb/1024).toFixed(1)+" GB";
  return mb.toFixed(0)+" MB";
}
function fmtUptime(s) {
  const d=Math.floor(s/86400),h=Math.floor((s%86400)/3600),m=Math.floor((s%3600)/60);
  return `${d}d ${h}h ${m}m`;
}
function fmtMs(ms) {
  if (ms>=60000) return (ms/60000).toFixed(1)+"m";
  if (ms>=1000)  return (ms/1000).toFixed(1)+"s";
  return ms+"ms";
}
const stateColor = s => s==="PRIMARY"?"#10b981":s==="SECONDARY"?"#06b6d4":"#ef4444";
const stateIcon  = s => s==="PRIMARY"?"👑":s==="SECONDARY"?"🔄":"❌";
const pctColor   = v => v>85?"#ef4444":v>70?"#f59e0b":"#10b981";

function Bar({label, value, sub, color}) {
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
        <span style={{fontSize:9,color:"#64748b",fontFamily:"'Inter',sans-serif",fontWeight:600,letterSpacing:".07em"}}>{label}</span>
        <span style={{fontSize:10,color:color||"#94a3b8",fontFamily:"'Inter',monospace",fontWeight:700}}>{value}%</span>
      </div>
      <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden",marginBottom:sub?2:0}}>
        <div style={{width:`${Math.min(value||0,100)}%`,height:"100%",borderRadius:2,background:color,transition:"width .5s"}}/>
      </div>
      {sub && <div style={{fontSize:9,color:"#475569",fontFamily:"'Inter',sans-serif"}}>{sub}</div>}
    </div>
  );
}

function NodeCard({node, rsInfo, metrics, srvstat, theme, onSelect, selected}) {
  const member = rsInfo?.members?.find(m=>m.name===node.host)||{};
  const state  = member.state||"UNKNOWN";
  const ops    = srvstat?.opcounters||{};
  const conn   = srvstat?.connections||{};
  const isSelected = selected===node.key;

  return (
    <div style={{background:theme.metricsBg,borderRadius:12,
      border:`2px solid ${isSelected?stateColor(state):stateColor(state)+"35"}`,
      overflow:"hidden",display:"flex",flexDirection:"column",
      boxShadow:isSelected?`0 0 20px ${stateColor(state)}30`:"none",
      transition:"all .2s",
    }}>
      {/* header */}
      <div style={{padding:"12px 16px",background:`${stateColor(state)}10`,
        borderBottom:`1px solid ${stateColor(state)}25`,
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:theme.text,fontFamily:"'Inter',sans-serif"}}>
            {stateIcon(state)} {node.label}
          </div>
          <div style={{fontSize:10,color:"#64748b",fontFamily:"'Fira Code',monospace",marginTop:2}}>{node.host}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
          <span style={{fontSize:11,fontWeight:700,padding:"3px 12px",borderRadius:5,
            background:`${stateColor(state)}18`,color:stateColor(state),
            border:`1px solid ${stateColor(state)}40`,fontFamily:"'Inter',sans-serif"}}>{state}</span>
          <span style={{fontSize:10,fontFamily:"'Inter',sans-serif",
            color:member.health===1?"#10b981":"#ef4444"}}>
            {member.health===1?"● Healthy":"● Down"}
          </span>
        </div>
      </div>

      <div style={{padding:"12px 16px",display:"flex",flexDirection:"column",gap:8,flex:1}}>
        {/* RS info */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          <div style={{background:theme.bg,borderRadius:6,padding:"6px 10px",border:`1px solid ${theme.border}`}}>
            <div style={{fontSize:9,color:"#64748b",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>RS UPTIME</div>
            <div style={{fontSize:12,fontWeight:700,color:theme.text,fontFamily:"'Inter',monospace"}}>{fmtUptime(member.uptime||0)}</div>
          </div>
          {member.lag_secs!=null?(
            <div style={{background:theme.bg,borderRadius:6,padding:"6px 10px",border:`1px solid ${theme.border}`}}>
              <div style={{fontSize:9,color:"#64748b",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>REPL LAG</div>
              <div style={{fontSize:12,fontWeight:700,fontFamily:"'Inter',monospace",
                color:member.lag_secs>10?"#ef4444":member.lag_secs>3?"#f59e0b":"#10b981"}}>{member.lag_secs}s</div>
            </div>
          ):(
            <div style={{background:theme.bg,borderRadius:6,padding:"6px 10px",border:`1px solid ${theme.border}`}}>
              <div style={{fontSize:9,color:"#64748b",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>PING</div>
              <div style={{fontSize:12,fontWeight:700,color:theme.text,fontFamily:"'Inter',monospace"}}>{member.ping_ms??0}ms</div>
            </div>
          )}
        </div>

        {/* Host metrics */}
        {metrics?(
          <div style={{background:theme.bg,borderRadius:8,padding:"10px 12px",border:`1px solid ${theme.border}`,display:"flex",flexDirection:"column",gap:7}}>
            <div style={{fontSize:9,color:"#64748b",fontWeight:700,fontFamily:"'Inter',sans-serif",letterSpacing:".1em"}}>HOST: {metrics.hostname}</div>
            <Bar label="CPU" value={metrics.cpu?.percent} color={pctColor(metrics.cpu?.percent)}
              sub={`${metrics.cpu?.cores} cores · load ${metrics.load?.["1m"]}`}/>
            <Bar label="RAM" value={metrics.ram?.percent} color={pctColor(metrics.ram?.percent)}
              sub={`${Math.round(metrics.ram?.used_mb/1024)}GB / ${Math.round(metrics.ram?.total_mb/1024)}GB`}/>
            {Object.entries(metrics.disks||{}).map(([mount,d])=>(
              <Bar key={mount} label={`DISK ${mount}`} value={d.percent} color={pctColor(d.percent)}
                sub={`${d.used_gb}GB / ${d.total_gb}GB · ${d.free_gb}GB free`}/>
            ))}
          </div>
        ):(
          <div style={{textAlign:"center",padding:10,color:"#475569",fontFamily:"'Inter',sans-serif",fontSize:11}}>Loading...</div>
        )}

        {/* Mongo stats */}
        {srvstat?.ok&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
              <div style={{background:theme.bg,borderRadius:6,padding:"6px 10px",border:`1px solid ${theme.border}`}}>
                <div style={{fontSize:9,color:"#64748b",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>CONNECTIONS</div>
                <div style={{fontSize:13,fontWeight:700,color:"#06b6d4",fontFamily:"'Inter',monospace"}}>{conn.current}</div>
                <div style={{fontSize:9,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>{conn.available} avail</div>
              </div>
              <div style={{background:theme.bg,borderRadius:6,padding:"6px 10px",border:`1px solid ${theme.border}`}}>
                <div style={{fontSize:9,color:"#64748b",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>MONGO MEM</div>
                <div style={{fontSize:13,fontWeight:700,color:"#8b5cf6",fontFamily:"'Inter',monospace"}}>{srvstat.memory?.resident_mb}MB</div>
                <div style={{fontSize:9,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>v{srvstat.version}</div>
              </div>
            </div>
            <div style={{background:theme.bg,borderRadius:8,padding:"8px 12px",border:`1px solid ${theme.border}`}}>
              <div style={{fontSize:9,color:"#64748b",fontWeight:700,fontFamily:"'Inter',sans-serif",letterSpacing:".1em",marginBottom:5}}>OPS</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4}}>
                {[["Q",ops.query,"#06b6d4"],["I",ops.insert,"#10b981"],["U",ops.update,"#f59e0b"],["D",ops.delete,"#ef4444"],["C",ops.command,"#8b5cf6"]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{fontSize:9,color:"#64748b",fontFamily:"'Inter',sans-serif",fontWeight:600}}>{l}</div>
                    <div style={{fontSize:11,fontWeight:700,color:c,fontFamily:"'Inter',monospace"}}>{(v||0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CollectionPanel({db, token, theme, onClose, role}) {
  const [colls,   setColls]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort,    setSort]    = useState("size");
  const [dropTarget, setDropTarget] = useState(null); // {name}
  const [dropStep,   setDropStep]   = useState("password"); // "password" | "confirm"
  const [dropPwd,    setDropPwd]    = useState("");
  const [dropError,  setDropError]  = useState("");
  const [dropping,   setDropping]   = useState(false);

  // date range delete state
  const [rangeTarget, setRangeTarget] = useState(null);
  const [rangeFields, setRangeFields] = useState([]);
  const [rangeField,  setRangeField]  = useState("");
  const [rangeFrom,   setRangeFrom]   = useState("");
  const [rangeTo,     setRangeTo]     = useState("");
  const [rangeStep,   setRangeStep]   = useState("password");
  const [rangePwd,    setRangePwd]    = useState("");
  const [rangeError,  setRangeError]  = useState("");
  const [ranging,     setRanging]     = useState(false);
  const [rangeResult, setRangeResult] = useState(null);
  const [rangeProgress, setRangeProgress] = useState(0);
  const rangeAbortRef = useRef(null);
  const [collSearch,  setCollSearch]  = useState("");

  const DROP_PASSWORD = "CodeBN360";

  const handleRangeCancel = () => {
    if (rangeAbortRef.current) rangeAbortRef.current.abort();
    setRangeTarget(null); setRanging(false); setRangeStep("password");
  };



  const handleRangeClick = async (cname) => {
    setRangeTarget(cname);
    setRangeStep("password");
    setRangePwd(""); setRangeError(""); setRangeResult(null);
    setRangeFrom(""); setRangeTo(""); setRangeField("");
    // fetch date fields
    try {
      const r = await fetch(`${PRI}/collections/${db}/${cname}/datefields`,
        {headers:{"X-Auth-Token":token}});
      const d = await r.json();
      setRangeFields(d.fields||["_id(timestamp)"]);
      setRangeField(d.fields?.[0]||"_id(timestamp)");
    } catch { setRangeFields(["_id(timestamp)"]); setRangeField("_id(timestamp)"); }
  };

  const handleRangePwdSubmit = () => {
    if (rangePwd===DROP_PASSWORD) { setRangeStep("form"); setRangeError(""); }
    else setRangeError("❌ Wrong password!");
  };

  const handleRangeDelete = async () => {
    if (!rangeField||!rangeFrom||!rangeTo) { setRangeError("Please fill all fields"); return; }
    setRanging(true); setRangeError(""); setRangeProgress(0);
    // show deleting step immediately
    setRangeStep("deleting");
    // animate counter while waiting
    const ticker = setInterval(()=>setRangeProgress(p=>p+Math.floor(Math.random()*500+100)), 300);
    try {
      const r = await fetch(`${PRI}/collections/${db}/${rangeTarget}/deleterange`, {
        method:"POST",
        headers:{"X-Auth-Token":token,"Content-Type":"application/json"},
        body: JSON.stringify({field:rangeField, from:rangeFrom, to:rangeTo}),
      });
      const d = await r.json();
      clearInterval(ticker);
      if (d.ok) { setRangeProgress(d.deleted); setRangeResult(d); setRangeStep("result"); }
      else { setRangeError(d.error||"Delete failed"); setRangeStep("form"); }
    } catch(e) { clearInterval(ticker); setRangeError("Network error"); setRangeStep("form"); }
    setRanging(false);
  };

  const handleDropClick = (cname) => {
    setDropTarget(cname);
    setDropStep("password");
    setDropPwd("");
    setDropError("");
  };

  const handlePasswordSubmit = () => {
    if (dropPwd === DROP_PASSWORD) {
      setDropStep("confirm");
      setDropError("");
    } else {
      setDropError("❌ Wrong password!");
    }
  };

  const handleConfirmDrop = async () => {
    setDropping(true);
    try {
      const r = await fetch(`${PRI}/collections/${db}/${dropTarget}/drop`, {
        method: "POST",
        headers: {"X-Auth-Token": token},
      });
      const d = await r.json();
      if (d.ok) {
        setColls(prev => prev.filter(c => c.name !== dropTarget));
        setDropTarget(null);
      } else {
        setDropError(d.error || "Drop failed");
      }
    } catch(e) {
      setDropError("Network error");
    }
    setDropping(false);
  };

  useEffect(()=>{
    fetch(`${PRI}/collections/${db}`,{headers:{"X-Auth-Token":token}})
      .then(r=>r.json())
      .then(d=>{ setColls(d.collections||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  },[db,token]);

  const sorted = [...colls].sort((a,b)=>
    sort==="size"?"":
    sort==="count"?b.count-a.count:
    sort==="index"?b.index_mb-a.index_mb:
    a.name.localeCompare(b.name)
  ).sort((a,b)=> sort==="size"?b.size_mb-a.size_mb:0);

  return (
    <div style={{position:"fixed",top:0,right:0,width:520,height:"100vh",
      background:theme.bgCard,borderLeft:`1px solid ${theme.border}`,
      zIndex:1000,display:"flex",flexDirection:"column",
      boxShadow:"-8px 0 32px rgba(0,0,0,0.4)"}}>
      {/* header */}
      <div style={{padding:"16px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.topbar,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:theme.text,fontFamily:"'Inter',sans-serif"}}>
            💾 {db}
          </div>
          <div style={{fontSize:11,color:"#64748b",fontFamily:"'Inter',sans-serif",marginTop:2}}>
            {colls.length} collections
          </div>
        </div>
        <button onClick={onClose} style={{background:theme.inputBg,border:`1px solid ${theme.border}`,
          color:theme.textMuted,borderRadius:6,padding:"4px 10px",cursor:"pointer",
          fontSize:11,fontFamily:"'Inter',sans-serif"}}>✕ Close</button>
      </div>

      {/* sort */}
      <div style={{display:"flex",gap:6,padding:"8px 16px",borderBottom:`1px solid ${theme.border}`,background:theme.bg}}>
        <span style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif",fontWeight:600,alignSelf:"center"}}>SORT:</span>
        {[["size","💾 Size"],["count","📦 Count"],["index","🔍 Index"],["name","🔤 Name"]].map(([k,l])=>(
          <button key={k} onClick={()=>setSort(k)} style={{
            fontSize:10,padding:"3px 8px",borderRadius:4,cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontWeight:600,
            background:sort===k?"rgba(6,182,212,0.12)":theme.inputBg,
            border:`1px solid ${sort===k?"rgba(6,182,212,0.35)":theme.border}`,
            color:sort===k?"#06b6d4":theme.textMuted,
          }}>{l}</button>
        ))}
      </div>

      {/* search */}
      <div style={{padding:"0 12px 10px"}}>
        <input value={collSearch} onChange={e=>setCollSearch(e.target.value)}
          placeholder="🔍 Search collection name..."
          style={{width:"100%",padding:"7px 12px",borderRadius:6,fontSize:12,
            fontFamily:"'Inter',sans-serif",boxSizing:"border-box",
            background:theme.inputBg,border:`1px solid ${theme.border}`,
            color:theme.text,outline:"none"}}/>
      </div>
      {/* list */}
      <div style={{flex:1,overflowY:"auto",padding:"8px 0"}}>
        {loading?(
          <div style={{textAlign:"center",padding:40,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>Loading collections...</div>
        ):sorted.filter(c=>!collSearch||c.name.toLowerCase().includes(collSearch.toLowerCase())).map((c,i)=>(
          <div key={i} style={{margin:"4px 12px",padding:"10px 14px",borderRadius:8,
            background:theme.metricsBg,border:`1px solid ${theme.border}`,
            display:"grid",gridTemplateColumns:"minmax(120px,1fr) 80px 80px 80px 80px 60px",gap:8,alignItems:"center"}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:theme.text,fontFamily:"'Inter',sans-serif",
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
              <div style={{fontSize:9,color:"#64748b",fontFamily:"'Inter',sans-serif",marginTop:1}}>
                {c.indexes} indexes · {c.avg_obj_kb}KB avg
              </div>
            </div>
            {[["STORAGE",fmt(c.storage_mb),"#8b5cf6"],
              ["INDEX",fmt(c.index_mb),"#f59e0b"],
              ["TOTAL DISK",fmt(c.storage_mb+c.index_mb),"#10b981"],
              ["DOCS",(c.count||0).toLocaleString(),"#06b6d4"]].map(([l,v,col])=>(
              <div key={l}>
                <div style={{fontSize:8,color:"#475569",fontWeight:600,fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>{l}</div>
                <div style={{fontSize:11,fontWeight:700,color:col,fontFamily:"'Inter',monospace"}}>{v}</div>
              </div>
            ))}
            {role!=="readonly"&&<div style={{display:"flex",flexDirection:"column",gap:5}}>
              <button onClick={()=>handleRangeClick(c.name)} style={{
                fontSize:10,padding:"5px 10px",borderRadius:6,cursor:"pointer",
                background:"linear-gradient(135deg,rgba(245,158,11,0.15),rgba(245,158,11,0.05))",
                border:"1px solid rgba(245,158,11,0.35)",
                color:"#f59e0b",fontFamily:"'Inter',sans-serif",fontWeight:700,
                whiteSpace:"nowrap",letterSpacing:".02em",
                boxShadow:"0 1px 4px rgba(245,158,11,0.1)"}}>
                📅 Range Del
              </button>
              <button onClick={()=>handleDropClick(c.name)} style={{
                fontSize:10,padding:"5px 10px",borderRadius:6,cursor:"pointer",
                background:"linear-gradient(135deg,rgba(239,68,68,0.15),rgba(239,68,68,0.05))",
                border:"1px solid rgba(239,68,68,0.35)",
                color:"#ef4444",fontFamily:"'Inter',sans-serif",fontWeight:700,
                whiteSpace:"nowrap",letterSpacing:".02em",
                boxShadow:"0 1px 4px rgba(239,68,68,0.1)"}}>
                🗑 Drop All
              </button>
            </div>}
          </div>
        ))}
      </div>
    {/* Date Range Delete Modal */}
    {rangeTarget&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",
        zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center"}}
        onClick={e=>e.stopPropagation()}>
        <div style={{background:theme.card,borderRadius:12,padding:"28px 32px",
          width:460,border:`2px solid rgba(245,158,11,0.4)`,
          boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}
          onClick={e=>e.stopPropagation()}>

          {rangeStep==="password"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#f59e0b",
                fontFamily:"'Inter',sans-serif",marginBottom:6}}>
                🔐 Authentication Required
              </div>
              <div style={{fontSize:12,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:16}}>
                Enter admin password to delete records from:<br/>
                <b style={{color:theme.text}}>{db}.{rangeTarget}</b>
              </div>
              <input type="password" value={rangePwd} autoFocus
                onChange={e=>setRangePwd(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handleRangePwdSubmit()}
                placeholder="Enter password..."
                style={{width:"100%",padding:"9px 12px",borderRadius:6,fontSize:13,
                  fontFamily:"'Inter',sans-serif",marginBottom:8,boxSizing:"border-box",
                  background:theme.inputBg,border:`1px solid ${rangeError?"#ef4444":theme.border}`,
                  color:theme.text,outline:"none"}}/>
              {rangeError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                fontFamily:"'Inter',sans-serif"}}>{rangeError}</div>}
              <div style={{display:"flex",gap:8,marginTop:8}}>
                <button onClick={handleRangePwdSubmit} style={{flex:1,padding:"8px",
                  borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,
                  background:"rgba(245,158,11,0.12)",border:"1px solid rgba(245,158,11,0.3)",
                  color:"#f59e0b",fontFamily:"'Inter',sans-serif"}}>Verify →</button>
                <button onClick={()=>setRangeTarget(null)} style={{flex:1,padding:"8px",
                  borderRadius:6,cursor:"pointer",fontSize:12,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
              </div>
            </>
          )}

          {rangeStep==="form"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#f59e0b",
                fontFamily:"'Inter',sans-serif",marginBottom:4}}>
                📅 Delete by Date Range
              </div>
              <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:16}}>
                Collection: <b style={{color:theme.text}}>{db}.{rangeTarget}</b>
              </div>

              {/* date field selector */}
              <div style={{marginBottom:12}}>
                <div style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
                  fontWeight:600,marginBottom:5}}>DATE FIELD</div>
                <select value={rangeField} onChange={e=>setRangeField(e.target.value)}
                  style={{width:"100%",padding:"8px 12px",borderRadius:6,fontSize:12,
                    fontFamily:"'Inter',monospace",background:theme.inputBg,
                    border:`1px solid ${theme.border}`,color:theme.text,outline:"none"}}>
                  {rangeFields.map(f=>(
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              {/* from date */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                <div>
                  <div style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
                    fontWeight:600,marginBottom:5}}>FROM DATE</div>
                  <input type="datetime-local" value={rangeFrom}
                    onChange={e=>setRangeFrom(e.target.value)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:6,fontSize:11,
                      fontFamily:"'Inter',sans-serif",boxSizing:"border-box",
                      background:theme.inputBg,border:`1px solid ${theme.border}`,
                      color:theme.text,outline:"none"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
                    fontWeight:600,marginBottom:5}}>TO DATE</div>
                  <input type="datetime-local" value={rangeTo}
                    onChange={e=>setRangeTo(e.target.value)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:6,fontSize:11,
                      fontFamily:"'Inter',sans-serif",boxSizing:"border-box",
                      background:theme.inputBg,border:`1px solid ${theme.border}`,
                      color:theme.text,outline:"none"}}/>
                </div>
              </div>

              <div style={{background:"rgba(245,158,11,0.08)",borderRadius:8,
                padding:"10px 14px",border:"1px solid rgba(245,158,11,0.2)",marginBottom:12}}>
                <div style={{fontSize:11,color:"#f59e0b",fontFamily:"'Inter',sans-serif"}}>
                  ⚠️ This will permanently delete all documents where <b>{rangeField}</b> is between the selected dates.
                </div>
              </div>

              {rangeError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                fontFamily:"'Inter',sans-serif"}}>{rangeError}</div>}

              <div style={{display:"flex",gap:8}}>
                <button onClick={handleRangeDelete} disabled={ranging} style={{flex:1,padding:"10px",
                  borderRadius:6,cursor:ranging?"not-allowed":"pointer",fontSize:12,fontWeight:700,
                  background:"rgba(245,158,11,0.15)",border:"1px solid rgba(245,158,11,0.4)",
                  color:"#f59e0b",fontFamily:"'Inter',sans-serif",opacity:ranging?0.7:1}}>
                  {ranging?"Deleting...":"🗑 Delete Records"}
                </button>
                <button onClick={()=>setRangeTarget(null)} style={{flex:1,padding:"10px",
                  borderRadius:6,cursor:"pointer",fontSize:12,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
              </div>
            </>
          )}

          {rangeStep==="deleting"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#f59e0b",
                fontFamily:"'Inter',sans-serif",marginBottom:20,textAlign:"center"}}>
                🗑 Deleting Records...
              </div>
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:48,fontWeight:800,color:"#ef4444",
                  fontFamily:"'Inter',monospace",letterSpacing:"-2px",
                  textShadow:"0 0 30px rgba(239,68,68,0.4)"}}>
                  {rangeProgress.toLocaleString()}
                </div>
                <div style={{fontSize:12,color:theme.textMuted,marginTop:8,
                  fontFamily:"'Inter',sans-serif"}}>records deleted so far...</div>
                <div style={{marginTop:20,height:4,background:theme.border,borderRadius:2,overflow:"hidden"}}>
                  <div style={{height:"100%",background:"linear-gradient(90deg,#ef4444,#f59e0b)",
                    borderRadius:2,width:"100%",
                    animation:"pulse 1s ease-in-out infinite"}}/>
                </div>
              </div>
              <div style={{fontSize:11,color:"#64748b",textAlign:"center",marginTop:12,
                fontFamily:"'Inter',sans-serif"}}>
                Please wait... this may take several minutes for large collections.
              </div>
              <div style={{marginTop:16,textAlign:"center"}}>
                <button onClick={handleRangeCancel}
                  style={{padding:"8px 24px",borderRadius:6,cursor:"pointer",fontSize:12,
                    background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
                    color:"#ef4444",fontFamily:"'Inter',sans-serif",fontWeight:600}}>
                  ✕ Cancel & Stop Delete
                </button>
              </div>
            </>
          )}

          {rangeStep==="result"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#10b981",
                fontFamily:"'Inter',sans-serif",marginBottom:16}}>
                ✅ Delete Complete
              </div>
              <div style={{background:"rgba(16,185,129,0.08)",borderRadius:8,
                padding:"16px",border:"1px solid rgba(16,185,129,0.2)",marginBottom:16}}>
                <div style={{fontSize:13,color:theme.text,fontFamily:"'Inter',sans-serif",marginBottom:6}}>
                  Collection: <b>{db}.{rangeTarget}</b>
                </div>
                <div style={{fontSize:22,fontWeight:800,color:"#10b981",
                  fontFamily:"'Inter',monospace"}}>{rangeResult?.deleted?.toLocaleString()}</div>
                <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginTop:2}}>
                  documents deleted
                </div>
              </div>
              <button onClick={()=>setRangeTarget(null)} style={{width:"100%",padding:"10px",
                borderRadius:6,cursor:"pointer",fontSize:13,fontWeight:700,
                background:"rgba(16,185,129,0.12)",border:"1px solid rgba(16,185,129,0.3)",
                color:"#10b981",fontFamily:"'Inter',sans-serif"}}>
                ✓ Done
              </button>
            </>
          )}
        </div>
      </div>
    )}

    {/* Date Range Delete Modal */}
        {/* Drop Modal */}
    {dropTarget&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",
        zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{background:theme.card,borderRadius:12,padding:"28px 32px",
          width:420,border:`2px solid rgba(239,68,68,0.4)`,
          boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>

          {dropStep==="password"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#ef4444",
                fontFamily:"'Inter',sans-serif",marginBottom:6}}>
                🔐 Authentication Required
              </div>
              <div style={{fontSize:12,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:16}}>
                Enter admin password to drop collection:<br/>
                <b style={{color:theme.text}}>{db}.{dropTarget}</b>
              </div>
              <input type="password" value={dropPwd}
                onChange={e=>setDropPwd(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handlePasswordSubmit()}
                placeholder="Enter password..."
                autoFocus
                style={{width:"100%",padding:"9px 12px",borderRadius:6,fontSize:13,
                  fontFamily:"'Inter',sans-serif",marginBottom:8,boxSizing:"border-box",
                  background:theme.inputBg,border:`1px solid ${dropError?"#ef4444":theme.border}`,
                  color:theme.text,outline:"none"}}/>
              {dropError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                fontFamily:"'Inter',sans-serif"}}>{dropError}</div>}
              <div style={{display:"flex",gap:8,marginTop:8}}>
                <button onClick={handlePasswordSubmit} style={{flex:1,padding:"8px",
                  borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,
                  background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",
                  color:"#ef4444",fontFamily:"'Inter',sans-serif"}}>Verify →</button>
                <button onClick={()=>setDropTarget(null)} style={{flex:1,padding:"8px",
                  borderRadius:6,cursor:"pointer",fontSize:12,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
              </div>
            </>
          )}

          {dropStep==="confirm"&&(
            <>
              <div style={{fontSize:16,fontWeight:700,color:"#ef4444",
                fontFamily:"'Inter',sans-serif",marginBottom:10}}>
                ⚠️ Confirm Collection Drop
              </div>
              <div style={{background:"rgba(239,68,68,0.08)",borderRadius:8,
                padding:"12px 16px",border:"1px solid rgba(239,68,68,0.2)",marginBottom:16}}>
                <div style={{fontSize:13,color:theme.text,fontFamily:"'Inter',sans-serif",marginBottom:4}}>
                  You are about to permanently drop:
                </div>
                <div style={{fontSize:15,fontWeight:700,color:"#ef4444",
                  fontFamily:"'Fira Code',monospace"}}>{db}.{dropTarget}</div>
                <div style={{fontSize:11,color:"#f59e0b",marginTop:8,
                  fontFamily:"'Inter',sans-serif"}}>
                  ⚠️ This action is IRREVERSIBLE. All data will be lost!
                </div>
              </div>
              {dropError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                fontFamily:"'Inter',sans-serif"}}>{dropError}</div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={handleConfirmDrop} disabled={dropping} style={{flex:1,padding:"10px",
                  borderRadius:6,cursor:dropping?"not-allowed":"pointer",fontSize:13,fontWeight:700,
                  background:"#ef4444",border:"none",
                  color:"#fff",fontFamily:"'Inter',sans-serif",opacity:dropping?0.7:1}}>
                  {dropping?"Dropping...":"🗑 Yes, Drop Collection"}
                </button>
                <button onClick={()=>setDropTarget(null)} style={{flex:1,padding:"10px",
                  borderRadius:6,cursor:"pointer",fontSize:13,fontWeight:700,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    </div>
  );
}

export default function DBPanel({ theme, token, role }) {
  const [rsInfo,   setRsInfo]   = useState(null);
  const [dbstats,  setDbstats]  = useState([]);
  const [nodeData, setNodeData] = useState({});
  const [queries,  setQueries]  = useState({all:[],slow:[]});
  const [slowThreshold, setSlowThreshold] = useState(1);
  const [showHistory,  setShowHistory]  = useState(false);
  const [history,      setHistory]      = useState([]);
  const [histLoading,  setHistLoading]  = useState(false);
  const [histHours,    setHistHours]    = useState(24);
  const [histMinSecs,  setHistMinSecs]  = useState(5);
  const [conns,    setConns]    = useState([]);
  const [tab,      setTab]      = useState("overview");
  const [diskTrend, setDiskTrend] = useState(null);
  const [showDiskTrend, setShowDiskTrend] = useState(false);
  const _setTab = (t) => { setTab(t); setSelDb(null); };
  const loadDiskTrend = async () => {
    try {
      const r = await fetch("/metrics-api/fsadb/disk-trend?days=14",
        { headers: {"X-Auth-Token": token} });
      const d = await r.json();
      setDiskTrend(d.trend);
    } catch(e) { console.error(e); }
  };
  const [qTab,     setQTab]     = useState("all");
  const [qSearch,  setQSearch]  = useState("");
  const [qUser,    setQUser]    = useState("all");
  const [connSearch,setConnSearch]=useState("");
  const [killTarget, setKillTarget] = useState(null);
  const [killStep,   setKillStep]   = useState("password");
  const [killPwd,    setKillPwd]    = useState("");
  const [killError,  setKillError]  = useState("");
  // eslint-disable-next-line no-unused-vars
  const [qLoading,   setQLoading]   = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [cLoading,   setCLoading]   = useState(false);
  const [killing,    setKilling]    = useState(false);

  const ADMIN_PWD = "CodeBN360";

  const handleKillClick = (q) => {
    setKillTarget(q);
    setKillStep("password");
    setKillPwd(""); setKillError("");
  };

  const handleKillPwdSubmit = () => {
    if (killPwd===ADMIN_PWD) { setKillStep("confirm"); setKillError(""); }
    else setKillError("❌ Wrong password!");
  };

  const handleKillConfirm = async () => {
    setKilling(true);
    try {
      const r = await fetch(`${PRI}/queries/kill`, {
        method:"POST",
        headers:{"X-Auth-Token":token,"Content-Type":"application/json"},
        body: JSON.stringify({opid: killTarget.opid}),
      });
      const d = await r.json();
      if (d.ok) {
        setQueries(prev=>({
          ...prev,
          all:  (prev.all||[]).filter(q=>q.opid!==killTarget.opid),
          slow: (prev.slow||[]).filter(q=>q.opid!==killTarget.opid),
        }));
        setKillTarget(null);
      } else setKillError(d.error||"Kill failed");
    } catch(e) { setKillError("Network error"); }
    setKilling(false);
  };
  const [dbSort,   setDbSort]   = useState("size");
  const [selDb,    setSelDb]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [lastUp,   setLastUp]   = useState(null);

  const loadHistory = async (hrs, minMs) => {
    setHistLoading(true);
    try {
      const h = hrs !== undefined ? hrs : histHours;
      const m = minMs !== undefined ? minMs : histMinSecs;
      const r = await fetch(`${PRI}/queries/history?hours=${h}&min_ms=${m*1000}&group=1`,
        { headers: { "X-Auth-Token": token } });
      const d = await r.json();
      setHistory(d.queries || []);
    } catch(e) { console.error(e); }
    setHistLoading(false);
  };

  const safeFetch = async (url, headers, timeout=15000) => {
    const ctrl = new AbortController();
    const tid  = setTimeout(()=>ctrl.abort(), timeout);
    try {
      const r = await fetch(url, {headers, signal:ctrl.signal});
      clearTimeout(tid);
      return await r.json();
    } catch(e) { clearTimeout(tid); return null; }
  };

  const fetchQueries = useCallback(async () => {
    const h = {"X-Auth-Token": token};
    setQLoading(true);
    const r = await safeFetch(`${PRI}/queries`, h, 20000);
    if (r) setQueries(r);
    setQLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // eslint-disable-next-line no-unused-vars
  const fetchConns = useCallback(async () => {
    const h = {"X-Auth-Token": token};
    setCLoading(true);
    const r = await safeFetch(`${PRI}/connections`, h, 20000);
    if (r) setConns(r?.connections||[]);
    setCLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchAll = useCallback(async () => {
    try {
      const h = {"X-Auth-Token": token};
      const [rsRes,dbRes,qRes,cRes] = await Promise.all([
        fetch(`${PRI}/rs`,          {headers:h}).then(r=>r.json()),
        fetch(`${PRI}/dbstats`,     {headers:h}).then(r=>r.json()),
        fetch(`${PRI}/queries`,     {headers:h}).then(r=>r.json()),
        fetch(`${PRI}/connections`, {headers:h}).then(r=>r.json()),
      ]);
      setRsInfo(rsRes);
      setDbstats(dbRes.databases||[]);
      setQueries(qRes);
      setConns(cRes.connections||[]);

      const nd = {};
      await Promise.all(DB_NODES.map(async node=>{
        try {
          const [m,s] = await Promise.all([
            fetch(`${node.url}/metrics`,      {headers:h}).then(r=>r.json()),
            fetch(`${node.url}/serverstatus`, {headers:h}).then(r=>r.json()),
          ]);
          nd[node.key]={metrics:m,srvstat:s};
        } catch { nd[node.key]={}; }
      }));
      setNodeData(nd);
      setLastUp(new Date().toLocaleTimeString());
    } catch(e){console.error(e);}
    finally{setLoading(false);}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[token]);

  useEffect(()=>{
    fetchAll();
    const id=setInterval(fetchAll,30000);
    return()=>clearInterval(id);
  },[fetchAll]);

  const sortedDbs = [...dbstats].sort((a,b)=>
    dbSort==="size"?b.size_mb-a.size_mb:
    dbSort==="objects"?b.objects-a.objects:
    dbSort==="collections"?b.collections-a.collections:
    a.name.localeCompare(b.name)
  );

  if(loading) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%",
      flexDirection:"column",gap:20,background:theme.bg}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{position:"relative",width:64,height:64}}>
        <div style={{position:"absolute",inset:0,borderRadius:"50%",
          border:"3px solid rgba(255,255,255,0.07)"}}/>
        <div style={{position:"absolute",inset:0,borderRadius:"50%",
          border:"3px solid transparent",borderTopColor:"#10b981",
          animation:"spin 0.8s linear infinite"}}/>
        <div style={{position:"absolute",inset:10,borderRadius:"50%",
          border:"2px solid transparent",borderTopColor:"#38bdf8",
          animation:"spin 1.3s linear infinite reverse"}}/>
      </div>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:15,fontWeight:700,color:"#10b981",
          fontFamily:"'Inter',sans-serif",marginBottom:6}}>Please wait...</div>
        <div style={{fontSize:13,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
          ⏳ Connecting to DB Cluster
        </div>
        <div style={{fontSize:12,color:theme.textFaint,fontFamily:"'Inter',sans-serif",marginTop:4}}>
          Fetching replica set & metrics
        </div>
      </div>
    </div>
  );

  const TABS=[["overview","🗄 Overview"],["databases","💾 Databases"],
              ["queries","⚡ Queries"],["connections","🔌 Connections"]];

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* tabs */}
      <div style={{display:"flex",gap:6,padding:"10px 20px",flexShrink:0,
        borderBottom:`1px solid ${theme.border}`,background:theme.bg,alignItems:"center"}}>
        <button onClick={()=>{setShowDiskTrend(v=>!v);if(!showDiskTrend)loadDiskTrend();}}
          style={{fontSize:11,padding:"5px 12px",borderRadius:6,cursor:"pointer",
            background:showDiskTrend?"rgba(16,185,129,0.15)":theme.inputBg,
            border:`1px solid ${showDiskTrend?"#10b981":theme.border}`,
            color:showDiskTrend?"#10b981":theme.textMuted,
            fontFamily:"'Inter',sans-serif",fontWeight:600,marginRight:8}}>
          📈 Disk Trend
        </button>
        {TABS.map(([k,l])=>(
          <button key={k} onClick={()=>_setTab(k)} style={{
            fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontWeight:600,
            background:tab===k?"rgba(16,185,129,0.12)":theme.inputBg,
            border:`1px solid ${tab===k?"rgba(16,185,129,0.35)":theme.border}`,
            color:tab===k?"#10b981":theme.textMuted,
          }}>
            {l}
            {k==="queries"&&queries.slow?.length>0&&(
              <span style={{marginLeft:6,background:"#ef4444",color:"#fff",
                borderRadius:10,padding:"1px 5px",fontSize:9,fontWeight:700}}>
                {queries.slow.length}
              </span>
            )}
            {k==="connections"&&(
              <span style={{marginLeft:6,background:"rgba(6,182,212,0.2)",color:"#06b6d4",
                borderRadius:10,padding:"1px 5px",fontSize:9,fontWeight:700}}>
                {conns.length}
              </span>
            )}
          </button>
        ))}
        <span style={{marginLeft:"auto",fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
          RS: <b style={{color:"#10b981"}}>{rsInfo?.rs_name}</b> · {lastUp}
        </span>
        <button onClick={fetchAll} style={{fontSize:10,padding:"4px 10px",borderRadius:5,cursor:"pointer",
          background:theme.inputBg,border:`1px solid ${theme.border}`,
          color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>⟳</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"16px 20px",position:"relative"}}>

        {/* OVERVIEW */}
        {/* Disk Trend Panel */}
        {showDiskTrend && (
          <div style={{margin:"8px 0 12px",background:theme.bgCard,
            border:"1px solid #10b98140",borderRadius:10,padding:14}}>
            <div style={{fontSize:12,fontWeight:700,color:"#10b981",marginBottom:10}}>
              📈 MongoDB /data Disk Growth (Last 14 days)
            </div>
            {!diskTrend && <div style={{color:theme.textMuted,fontSize:12}}>Loading...</div>}
            {diskTrend && Object.entries(diskTrend).map(([srv,data])=>(
              <div key={srv} style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:theme.text,marginBottom:6}}>
                  {srv==="db"?"🟡 DB Primary":srv==="dbsec1"?"🔵 DB Secondary-1":"🟢 DB Secondary-2"}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"100px 80px 80px 80px 80px",
                  gap:6,fontSize:9,fontWeight:700,color:theme.textMuted,
                  borderBottom:`1px solid ${theme.border}`,paddingBottom:4,marginBottom:4}}>
                  <span>DATE</span><span>/data %</span><span>GROWTH</span><span>/ %</span><span>STATUS</span>
                </div>
                {[...data].reverse().map((d,i)=>(
                  <div key={i} style={{display:"grid",
                    gridTemplateColumns:"100px 80px 80px 80px 80px",
                    gap:6,padding:"4px 0",fontSize:11,
                    fontFamily:"'JetBrains Mono',monospace",
                    borderBottom:`1px solid ${theme.border}20`}}>
                    <span style={{color:theme.textMuted}}>{d.day}</span>
                    <span style={{color:d.max_data>85?"#ef4444":d.max_data>75?"#f59e0b":"#10b981",
                      fontWeight:700}}>{d.max_data}%</span>
                    <span style={{color:d.growth>0.5?"#ef4444":d.growth>0?"#f59e0b":"#10b981",
                      fontWeight:700}}>{d.growth>0?"+":""}{d.growth}%</span>
                    <span style={{color:theme.textMuted}}>{d.avg_root}%</span>
                    <span style={{color:d.max_data>88?"#ef4444":d.max_data>80?"#f59e0b":"#10b981",
                      fontSize:9,fontWeight:700}}>
                      {d.max_data>88?"CRITICAL":d.max_data>80?"WARNING":"OK"}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab==="overview"&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
            {DB_NODES.map(node=>(
              <NodeCard key={node.key} node={node} rsInfo={rsInfo}
                metrics={nodeData[node.key]?.metrics}
                srvstat={nodeData[node.key]?.srvstat}
                theme={theme}/>
            ))}
          </div>
        )}

        {/* DATABASES */}
        {tab==="databases"&&(
          <div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
              <span style={{fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif",fontWeight:600}}>SORT:</span>
              {[["size","💾 Size"],["objects","📦 Objects"],["collections","📁 Collections"],["name","🔤 Name"]].map(([k,l])=>(
                <button key={k} onClick={()=>setDbSort(k)} style={{
                  fontSize:10,padding:"3px 10px",borderRadius:5,cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontWeight:600,
                  background:dbSort===k?"rgba(16,185,129,0.12)":theme.inputBg,
                  border:`1px solid ${dbSort===k?"rgba(16,185,129,0.35)":theme.border}`,
                  color:dbSort===k?"#10b981":theme.textMuted,
                }}>{l}</button>
              ))}
              <span style={{marginLeft:"auto",fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
                {sortedDbs.length} databases · click to view collections
              </span>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {sortedDbs.map((db,i)=>(
                <div key={i} onClick={()=>setSelDb(db.name)}
                  style={{background:theme.metricsBg,borderRadius:8,
                    padding:"12px 16px",border:`1px solid ${selDb===db.name?"#06b6d4":"${theme.border}"}`,
                    display:"grid",
                    gridTemplateColumns:"minmax(140px,1fr) 120px 120px 130px 110px",
                    gap:12,alignItems:"center",cursor:"pointer",transition:"all .15s",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#06b6d440"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=theme.border}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#06b6d4",
                      fontFamily:"'Inter',sans-serif",textDecoration:"underline",
                      textDecorationColor:"#06b6d430"}}>{db.name}</div>
                    <div style={{fontSize:10,color:theme.textMuted,marginTop:2,
                      fontFamily:"'Inter',sans-serif"}}>{db.collections} collections · click to explore</div>
                  </div>
                  {[["STORAGE",fmt(db.storage_mb),"#8b5cf6"],
                    ["INDEXES",fmt(db.index_mb),"#f59e0b"],
                    ["TOTAL DISK",fmt(db.storage_mb+db.index_mb),"#10b981"],
                    ["OBJECTS",(db.objects||0).toLocaleString(),"#06b6d4"]].map(([l,v,c])=>(
                    <div key={l}>
                      <div style={{fontSize:9,color:theme.textMuted,fontWeight:600,
                        fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:3}}>{l}</div>
                      <div style={{fontSize:12,fontWeight:700,color:c,fontFamily:"'Inter',monospace"}}>{v}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* QUERIES */}
        {tab==="queries"&&(
          <div>
            {/* long running > 3 min */}
            {(()=>{
              const longRunning = [...(queries.all||[])].filter(q=>q.duration_ms>=30000);
              if (!longRunning.length) return null;
              return (
                <div style={{marginBottom:16,borderRadius:8,
                  border:"2px solid rgba(239,68,68,0.4)",overflow:"hidden"}}>
                  <div style={{background:"rgba(239,68,68,0.12)",padding:"8px 14px",
                    display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:11,fontWeight:700,color:"#ef4444",
                      fontFamily:"'Inter',sans-serif",letterSpacing:".08em"}}>
                      🚨 LONG RUNNING (&gt;30s) — {longRunning.length} found
                    </span>
                    <button onClick={fetchQueries} style={{marginLeft:"auto",fontSize:10,
                      padding:"2px 8px",borderRadius:4,cursor:"pointer",
                      background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",
                      color:"#ef4444",fontFamily:"'Inter',sans-serif"}}>↺ Refresh</button>
                  </div>
                  {longRunning.map((q,i)=>(
                    <div key={i} style={{padding:"12px 16px",
                      borderBottom:"1px solid rgba(239,68,68,0.15)",
                      background:i%2===0?"rgba(239,68,68,0.04)":"rgba(239,68,68,0.02)"}}>
                      <div style={{display:"grid",
                        gridTemplateColumns:"100px 120px 120px 1fr 90px",
                        gap:8,alignItems:"center",marginBottom:6}}>
                        {/* duration */}
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>DURATION</div>
                          <div style={{fontSize:15,fontWeight:800,color:"#ef4444",
                            fontFamily:"'Inter',monospace"}}>{fmtMs(q.duration_ms)}</div>
                        </div>
                        {/* op type */}
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>OPERATION</div>
                          <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:4,
                            background:"rgba(6,182,212,0.12)",color:"#06b6d4",
                            border:"1px solid rgba(6,182,212,0.25)",fontFamily:"'Inter',sans-serif"}}>
                            {q.op?.toUpperCase()||"UNKNOWN"}
                          </span>
                        </div>
                        {/* user */}
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>USER</div>
                          <div style={{fontSize:11,color:"#a78bfa",fontFamily:"'Inter',monospace",
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            👤 {q.user}
                          </div>
                        </div>
                        {/* namespace */}
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>NAMESPACE</div>
                          <div style={{fontSize:11,fontWeight:600,color:theme.text,
                            fontFamily:"'Fira Code',monospace",overflow:"hidden",
                            textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{q.ns||"—"}</div>
                        </div>
                        {/* kill button */}
                        {role!=="readonly"&&<button onClick={()=>handleKillClick(q)} style={{
                          fontSize:11,padding:"6px 12px",borderRadius:5,cursor:"pointer",
                          background:"#ef4444",border:"none",color:"#fff",
                          fontFamily:"'Inter',sans-serif",fontWeight:700,whiteSpace:"nowrap"}}>
                          ⚡ Kill
                        </button>}
                      </div>
                      {/* second row: client + command */}
                      <div style={{display:"grid",gridTemplateColumns:"220px 1fr",gap:8}}>
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>CLIENT IP</div>
                          <div style={{fontSize:10,color:"#94a3b8",fontFamily:"'Fira Code',monospace"}}>
                            🌐 {q.client||"—"}
                          </div>
                        </div>
                        <div>
                          <div style={{fontSize:8,color:"#64748b",fontWeight:600,
                            fontFamily:"'Inter',sans-serif",letterSpacing:".08em",marginBottom:2}}>COMMAND</div>
                          <div style={{fontSize:10,color:"#64748b",fontFamily:"'Fira Code',monospace",
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                            padding:"3px 6px",background:"rgba(0,0,0,0.3)",borderRadius:4}}>
                            {String(q.command||"—").slice(0,120)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{display:"flex",gap:6,marginBottom:12,alignItems:"center",flexWrap:"wrap"}}>
              {[["all","All Queries"],["slow","🐢 Slow"]].map(([k,l])=>(
                <button key={k} onClick={()=>setQTab(k)} style={{
                  fontSize:11,padding:"5px 14px",borderRadius:6,cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontWeight:600,
                  background:qTab===k?"rgba(239,68,68,0.12)":theme.inputBg,
                  border:`1px solid ${qTab===k?"rgba(239,68,68,0.35)":theme.border}`,
                  color:qTab===k?"#ef4444":theme.textMuted,
                }}>
                  {l}
                  <span style={{marginLeft:6,background:qTab===k?"rgba(239,68,68,0.2)":"rgba(255,255,255,0.05)",
                    color:qTab===k?"#ef4444":theme.textMuted,borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:700}}>
                    {qTab==="slow"
                      ? (queries.all||[]).filter(q=>q.duration_ms>=slowThreshold*1000).length
                      : (queries.all||[]).length}
                  </span>
                </button>
              ))}
              {/* Threshold input - only show when slow tab active */}
              {qTab==="slow" && (
                <div style={{display:"flex",alignItems:"center",gap:6,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  borderRadius:6,padding:"3px 10px"}}>
                  <span style={{fontSize:11,color:theme.textMuted,fontWeight:600}}>Min:</span>
                  <input type="number" min="0" step="0.5" value={slowThreshold}
                    onChange={e=>setSlowThreshold(parseFloat(e.target.value)||0)}
                    style={{width:50,background:"transparent",border:"none",
                      color:"#ef4444",fontFamily:"'Inter',sans-serif",
                      fontSize:12,fontWeight:700,outline:"none",textAlign:"center"}}/>
                  <span style={{fontSize:11,color:theme.textMuted,fontWeight:600}}>sec</span>
                </div>
              )}
              {qTab==="slow" && (
                <button onClick={()=>{setShowHistory(v=>!v);if(!showHistory)loadHistory();}} style={{
                  background:showHistory?"rgba(167,139,250,0.15)":theme.inputBg,
                  border:`1px solid ${showHistory?"#a78bfa":theme.border}`,
                  color:showHistory?"#a78bfa":theme.textMuted,
                  borderRadius:6,padding:"5px 12px",cursor:"pointer",
                  fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600,
                }}>📜 History (24h)</button>
              )}
              <span style={{marginLeft:"auto",fontSize:10,color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>
                sorted by duration
              </span>
            </div>
            {/* Slow Query History Panel */}
            {showHistory && qTab==="slow" && (
              <div style={{background:theme.bgCard,border:`1px solid #a78bfa40`,
                borderRadius:10,padding:16,marginBottom:12}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#a78bfa"}}>📜 Slow Query History</span>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,color:theme.textMuted}}>Last</span>
                    <select value={histHours} onChange={e=>{setHistHours(+e.target.value);}}
                      style={{background:theme.inputBg,border:`1px solid ${theme.border}`,
                        color:theme.text,borderRadius:5,padding:"3px 8px",fontSize:11,outline:"none"}}>
                      <option value={1}>1 hour</option>
                      <option value={6}>6 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                    </select>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{fontSize:11,color:theme.textMuted}}>Min</span>
                    <input type="number" min="1" value={histMinSecs}
                      onChange={e=>setHistMinSecs(+e.target.value||1)}
                      style={{width:45,background:theme.inputBg,border:`1px solid ${theme.border}`,
                        color:"#ef4444",borderRadius:5,padding:"3px 6px",
                        fontSize:11,fontWeight:700,outline:"none",textAlign:"center"}}/>
                    <span style={{fontSize:11,color:theme.textMuted}}>sec</span>
                  </div>
                  <button onClick={()=>loadHistory(histHours, histMinSecs)} style={{
                    background:"rgba(167,139,250,0.1)",border:"1px solid #a78bfa40",
                    color:"#a78bfa",borderRadius:5,padding:"4px 10px",cursor:"pointer",
                    fontFamily:"'Inter',sans-serif",fontSize:11,fontWeight:600}}>
                    ↻ Apply
                  </button>
                  {histLoading && <span style={{fontSize:11,color:theme.textMuted}}>Loading...</span>}
                  <span style={{fontSize:11,color:theme.textMuted,marginLeft:"auto"}}>
                    {history.length} queries found
                  </span>
                </div>
                <div style={{maxHeight:300,overflowY:"auto"}}>
                  {history.length===0 && !histLoading && (
                    <div style={{textAlign:"center",padding:20,color:theme.textMuted,fontSize:12}}>
                      ✅ No slow queries in this period
                    </div>
                  )}
                  {/* Summary */}
                  <div style={{display:"flex",gap:12,marginBottom:10,flexWrap:"wrap"}}>
                    {[
                      ["Total Slow", history.reduce((a,q)=>a+(q.count||1),0), "#ef4444"],
                      ["Unique Patterns", history.length, "#a78bfa"],
                      ["Slowest", history[0]?`${history[0].max_secs}s`:"—", "#f59e0b"],
                    ].map(([l,v,c])=>(
                      <div key={l} style={{background:theme.inputBg,borderRadius:6,
                        padding:"6px 12px",display:"flex",gap:8,alignItems:"center"}}>
                        <span style={{fontSize:10,color:theme.textMuted}}>{l}:</span>
                        <span style={{fontSize:13,fontWeight:700,color:c,
                          fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
                      </div>
                    ))}
                  </div>
                  {/* Header */}
                  <div style={{display:"grid",
                    gridTemplateColumns:"120px 50px 90px 65px 1fr 110px 80px",
                    gap:8,padding:"6px 10px",fontSize:9,fontWeight:700,
                    color:theme.textMuted,letterSpacing:".08em",
                    borderBottom:`1px solid ${theme.border}`,marginBottom:6}}>
                    <span>TIMESTAMP</span><span>CNT</span><span>DURATION</span>
                    <span>OP</span><span>NAMESPACE · QUERY</span><span>USER · CLIENT</span><span>AFFECTED</span>
                  </div>
                  {history.map((q,i)=>(
                    <div key={i} style={{borderRadius:8,marginBottom:4,
                      background:i%2===0?theme.inputBg:"transparent",
                      border:`1px solid ${q.max_secs>30?"rgba(239,68,68,0.25)":q.max_secs>10?"rgba(245,158,11,0.2)":"transparent"}`}}>
                      <div style={{display:"grid",
                        gridTemplateColumns:"120px 50px 90px 65px 1fr 110px 80px",
                        gap:8,padding:"9px 10px",
                        fontSize:11,fontFamily:"'JetBrains Mono',monospace",alignItems:"start"}}>
                        {/* Timestamp */}
                        <div style={{fontSize:10,color:theme.textMuted}}>
                          <div>{q.ts?.split(" ")[0]||"—"}</div>
                          <div style={{color:"#a78bfa"}}>{q.ts?.split(" ")[1]||""}</div>
                        </div>
                        {/* Count */}
                        <span style={{background:"rgba(167,139,250,0.15)",color:"#a78bfa",
                          fontWeight:700,padding:"2px 5px",borderRadius:4,textAlign:"center",
                          fontSize:10,height:"fit-content"}}>
                          {q.count||1}x
                        </span>
                        {/* Duration */}
                        <div>
                          <span style={{color:q.max_secs>30?"#ef4444":q.max_secs>10?"#f59e0b":"#a78bfa",
                            fontWeight:800,fontSize:14}}>{q.max_secs}s</span>
                          {q.count>1 && <div style={{fontSize:9,color:theme.textMuted}}>min:{q.min_secs}s</div>}
                        </div>
                        {/* Op */}
                        <span style={{
                          background:q.op==="insert"?"rgba(16,185,129,0.15)":q.op==="query"?"rgba(6,182,212,0.15)":q.op==="update"?"rgba(245,158,11,0.15)":"rgba(139,92,246,0.15)",
                          color:q.op==="insert"?"#10b981":q.op==="query"?"#06b6d4":q.op==="update"?"#f59e0b":"#8b5cf6",
                          padding:"3px 8px",borderRadius:4,fontWeight:700,fontSize:10,
                          textTransform:"uppercase",height:"fit-content"}}>
                          {q.op}
                        </span>
                        {/* Namespace + Query */}
                        <div>
                          <div style={{color:theme.text,fontWeight:700,fontSize:12}}>
                            {q.db}.{q.coll}
                          </div>
                          <div style={{fontSize:10,color:theme.textMuted,marginTop:3}}>
                            {q.query && !q.query.includes("insert 0 doc")
                              ? q.query
                              : q.op==="insert"
                                ? `Bulk insert into ${q.coll} (${q.affected||0} docs/op)`
                                : q.plan||"—"}
                          </div>
                        </div>
                        {/* User + Client */}
                        <div>
                          <div style={{color:"#06b6d4",fontSize:10}}>
                            👤 {(q.users||[])[0]?.split("@")[0]||"—"}
                          </div>
                          <div style={{color:theme.textMuted,fontSize:10,marginTop:2}}>
                            🌐 {(q.clients||[])[0]||"—"}
                          </div>
                        </div>
                        {/* Affected */}
                        <span style={{color:q.affected>0?"#10b981":theme.textMuted,
                          fontWeight:700,fontSize:11}}>
                          {q.affected>0?`${q.affected} docs`:"—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* search */}
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input value={qSearch} onChange={e=>setQSearch(e.target.value)}
                placeholder="🔍 Search namespace, client, command..."
                style={{flex:1,padding:"7px 12px",borderRadius:6,fontSize:12,
                  fontFamily:"'Inter',sans-serif",
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.text,outline:"none"}}/>
              {qSearch&&(
                <button onClick={()=>setQSearch("")} style={{
                  padding:"6px 12px",borderRadius:6,cursor:"pointer",fontSize:11,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>✕</button>
              )}
            </div>
            {(qTab==="slow"
              ? (queries.all||[]).filter(q=>q.duration_ms>=slowThreshold*1000)
              : (queries.all||[])).length===0?(
              <div style={{textAlign:"center",padding:"50px 0",color:"#10b981",
                fontFamily:"'Inter',sans-serif",fontSize:14}}>
                ✅ {qTab==="slow"?"No slow queries!":"No active queries"}
              </div>
            ):[...(qTab==="slow"
              ? (queries.all||[]).filter(q=>q.duration_ms>=slowThreshold*1000)
              : (queries.all||[]))]
              .filter(q=>
                (qUser==="all"||q.user===qUser) &&
                (!qSearch||
                  q.ns?.toLowerCase().includes(qSearch.toLowerCase())||
                  q.client?.toLowerCase().includes(qSearch.toLowerCase())||
                  q.user?.toLowerCase().includes(qSearch.toLowerCase())||
                  String(q.command||"").toLowerCase().includes(qSearch.toLowerCase())
                )
              )
              .sort((a,b)=>b.duration_ms-a.duration_ms)
              .map((q,i)=>(
              <div key={i} style={{background:theme.metricsBg,borderRadius:8,
                padding:"12px 16px",marginBottom:6,
                border:`1px solid ${q.duration_ms>5000?"rgba(239,68,68,0.3)":q.duration_ms>1000?"rgba(245,158,11,0.3)":theme.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:4,
                      background:"rgba(139,92,246,0.12)",color:"#8b5cf6",
                      border:"1px solid rgba(139,92,246,0.25)",fontFamily:"'Inter',sans-serif"}}>
                      {q.op?.toUpperCase()||"CMD"}
                    </span>
                    <span style={{fontSize:12,color:theme.text,fontFamily:"'Fira Code',monospace",fontWeight:600}}>{q.ns}</span>
                    <span style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif",
                      background:theme.bg,padding:"1px 6px",borderRadius:4,border:`1px solid ${theme.border}`}}>
                      👤 {q.user}
                    </span>
                    {q.wait_lock&&(
                      <span style={{fontSize:10,color:"#ef4444",fontFamily:"'Inter',sans-serif",
                        background:"rgba(239,68,68,0.1)",padding:"1px 6px",borderRadius:4}}>⚠ waiting for lock</span>
                    )}
                  </div>
                  <span style={{fontSize:14,fontWeight:800,fontFamily:"'Inter',monospace",
                    color:q.duration_ms>5000?"#ef4444":q.duration_ms>1000?"#f59e0b":"#94a3b8",
                    flexShrink:0,marginLeft:12}}>
                    {fmtMs(q.duration_ms)}
                  </span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  <div style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>
                    🌐 <b>Client:</b> {q.client}
                  </div>
                  {q.plan_summary&&(
                    <div style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>
                      📋 <b>Plan:</b> {q.plan_summary}
                    </div>
                  )}
                </div>
                {q.command&&(
                  <div style={{marginTop:8,padding:"6px 10px",background:"#020408",borderRadius:6,
                    border:"1px solid rgba(255,255,255,0.05)",
                    fontSize:10,color:"#94a3b8",fontFamily:"'Fira Code',monospace",
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {q.command}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Kill Query Modal */}
        {killTarget&&(
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",
            zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <div style={{background:theme.card,borderRadius:12,padding:"28px 32px",
              width:440,border:"2px solid rgba(239,68,68,0.5)",
              boxShadow:"0 20px 60px rgba(0,0,0,0.6)"}}>

              {killStep==="password"&&(
                <>
                  <div style={{fontSize:16,fontWeight:700,color:"#ef4444",
                    fontFamily:"'Inter',sans-serif",marginBottom:6}}>🔐 Authentication Required</div>
                  <div style={{fontSize:12,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:16}}>
                    Enter admin password to kill query:<br/>
                    <b style={{color:"#f59e0b",fontFamily:"'Fira Code',monospace",fontSize:11}}>opid: {killTarget.opid} · {killTarget.ns}</b>
                  </div>
                  <input type="password" value={killPwd} autoFocus
                    onChange={e=>setKillPwd(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleKillPwdSubmit()}
                    placeholder="Enter password..."
                    style={{width:"100%",padding:"9px 12px",borderRadius:6,fontSize:13,
                      fontFamily:"'Inter',sans-serif",marginBottom:8,boxSizing:"border-box",
                      background:theme.inputBg,border:`1px solid ${killError?"#ef4444":theme.border}`,
                      color:theme.text,outline:"none"}}/>
                  {killError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                    fontFamily:"'Inter',sans-serif"}}>{killError}</div>}
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <button onClick={handleKillPwdSubmit} style={{flex:1,padding:"8px",
                      borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700,
                      background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.3)",
                      color:"#ef4444",fontFamily:"'Inter',sans-serif"}}>Verify →</button>
                    <button onClick={()=>setKillTarget(null)} style={{flex:1,padding:"8px",
                      borderRadius:6,cursor:"pointer",fontSize:12,
                      background:theme.inputBg,border:`1px solid ${theme.border}`,
                      color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                  </div>
                </>
              )}

              {killStep==="confirm"&&(
                <>
                  <div style={{fontSize:16,fontWeight:700,color:"#ef4444",
                    fontFamily:"'Inter',sans-serif",marginBottom:10}}>⚡ Kill Query?</div>
                  <div style={{background:"rgba(239,68,68,0.08)",borderRadius:8,
                    padding:"12px 16px",border:"1px solid rgba(239,68,68,0.2)",marginBottom:16}}>
                    <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:4}}>opid</div>
                    <div style={{fontSize:15,fontWeight:700,color:"#ef4444",fontFamily:"'Fira Code',monospace",marginBottom:8}}>{killTarget.opid}</div>
                    <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:2}}>namespace</div>
                    <div style={{fontSize:12,color:theme.text,fontFamily:"'Fira Code',monospace",marginBottom:8}}>{killTarget.ns}</div>
                    <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginBottom:2}}>running for</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#f59e0b",fontFamily:"'Inter',monospace"}}>{fmtMs(killTarget.duration_ms)}</div>
                  </div>
                  {killError&&<div style={{color:"#ef4444",fontSize:11,marginBottom:8,
                    fontFamily:"'Inter',sans-serif"}}>{killError}</div>}
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={handleKillConfirm} disabled={killing} style={{flex:1,padding:"10px",
                      borderRadius:6,cursor:killing?"not-allowed":"pointer",fontSize:13,fontWeight:700,
                      background:"#ef4444",border:"none",color:"#fff",
                      fontFamily:"'Inter',sans-serif",opacity:killing?0.7:1}}>
                      {killing?"Killing...":"⚡ Kill Query"}
                    </button>
                    <button onClick={()=>setKillTarget(null)} style={{flex:1,padding:"10px",
                      borderRadius:6,cursor:"pointer",fontSize:13,
                      background:theme.inputBg,border:`1px solid ${theme.border}`,
                      color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* CONNECTIONS */}
        {tab==="connections"&&(
          <div>
            {/* search */}
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input value={connSearch}
                onChange={e=>setConnSearch(e.target.value)}
                placeholder="🔍 Search by username, IP, namespace..."
                style={{flex:1,padding:"7px 12px",borderRadius:6,fontSize:12,
                  fontFamily:"'Inter',sans-serif",
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.text,outline:"none"}}/>
              {connSearch&&(
                <button onClick={()=>setConnSearch("")} style={{
                  padding:"6px 12px",borderRadius:6,cursor:"pointer",fontSize:11,
                  background:theme.inputBg,border:`1px solid ${theme.border}`,
                  color:theme.textMuted,fontFamily:"'Inter',sans-serif"}}>✕</button>
              )}
            </div>
            <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",
              fontWeight:700,letterSpacing:".1em",marginBottom:12}}>
              {conns.length} ACTIVE CONNECTIONS — sorted by duration
            </div>
            {(()=>{
              const filtered = [...conns]
                .filter(c=> !connSearch ||
                  c.user?.toLowerCase().includes(connSearch.toLowerCase()) ||
                  c.client?.toLowerCase().includes(connSearch.toLowerCase()) ||
                  c.ns?.toLowerCase().includes(connSearch.toLowerCase()) ||
                  c.op?.toLowerCase().includes(connSearch.toLowerCase())
                )
                .sort((a,b)=>{
                  const aInt = a.user==="system"||a.user==="__system";
                  const bInt = b.user==="system"||b.user==="__system";
                  if (aInt!==bInt) return aInt?1:-1;
                  return b.duration_ms-a.duration_ms;
                });
              if (filtered.length===0) return (
                <div style={{textAlign:"center",padding:"50px 0",color:"#10b981",
                  fontFamily:"'Inter',sans-serif",fontSize:14}}>
                  {connSearch ? `No connections matching "${connSearch}"` : "✅ No active connections"}
                </div>
              );
              return filtered.map((c,i)=>(
              <div key={i} style={{background:theme.metricsBg,borderRadius:8,
                padding:"12px 16px",marginBottom:6,border:`1px solid ${theme.border}`,
                display:"grid",gridTemplateColumns:"1fr auto",gap:12,alignItems:"start"}}>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:4,
                      background:"rgba(6,182,212,0.12)",color:"#06b6d4",
                      border:"1px solid rgba(6,182,212,0.25)",fontFamily:"'Inter',sans-serif"}}>
                      {c.op?.toUpperCase()||"IDLE"}
                    </span>
                    <span style={{fontSize:12,fontWeight:600,color:theme.text,fontFamily:"'Fira Code',monospace"}}>{c.ns||"—"}</span>
                    <span style={{fontSize:10,color:"#64748b",background:theme.bg,padding:"1px 6px",
                      borderRadius:4,border:`1px solid ${theme.border}`,fontFamily:"'Inter',sans-serif"}}>
                      👤 {c.user}
                    </span>
                    {c.wait_lock&&(
                      <span style={{fontSize:10,color:"#f59e0b",fontFamily:"'Inter',sans-serif"}}>⚠ waiting lock</span>
                    )}
                  </div>
                  <div style={{display:"flex",gap:16}}>
                    <span style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>🌐 {c.client}</span>
                    <span style={{fontSize:10,color:"#64748b",fontFamily:"'Inter',sans-serif"}}>🔖 {c.desc}</span>
                  </div>
                  {c.command&&(
                    <div style={{padding:"5px 8px",background:"#020408",borderRadius:5,
                      border:"1px solid rgba(255,255,255,0.05)",
                      fontSize:10,color:"#64748b",fontFamily:"'Fira Code',monospace",
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {c.command}
                    </div>
                  )}
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:14,fontWeight:800,fontFamily:"'Inter',monospace",
                    color:c.duration_ms>5000?"#ef4444":c.duration_ms>1000?"#f59e0b":"#94a3b8"}}>
                    {fmtMs(c.duration_ms)}
                  </div>
                  <div style={{fontSize:9,color:"#475569",fontFamily:"'Inter',sans-serif",marginTop:2}}>
                    opid: {c.opid}
                  </div>
                </div>
              </div>
            ));
            })()}
          </div>
        )}
      </div>

      {/* Collection side panel */}
      {selDb&&(
        <CollectionPanel db={selDb} token={token} theme={theme} role={role} onClose={()=>setSelDb(null)}/>
      )}
    </div>
  );
}
